import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, FlaskConical, Cable, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { retryOrderActionApi, fetchCredentialedOperators } from '@/hooks/useTickets';
import type { OrderActionDto } from '@uniticket/shared';

// ── Shared visual tone for an order-automation result ──────────
// DRY_RUN (simulated) is ALWAYS rendered in blue/info chrome — never success-green or fail-red —
// so a simulation can never be mistaken for a real placed order.

type Tone = 'success' | 'failed' | 'pending' | 'dryrun' | 'dryrunFailed';

function toneOf(oa: OrderActionDto): Tone {
  if (oa.mode === 'DRY_RUN') return oa.status === 'SIMULATED_FAILED' ? 'dryrunFailed' : 'dryrun';
  if (oa.status === 'SUCCESS') return 'success';
  if (oa.status === 'FAILED') return 'failed';
  return 'pending'; // PENDING / SENT
}

const BADGE_CLASS: Record<Tone, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  failed: 'border-red-200 bg-red-50 text-red-700',
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
  dryrun: 'border-blue-200 bg-blue-50 text-blue-700',
  dryrunFailed: 'border-blue-200 bg-blue-50 text-blue-700',
};

export function OrderStatusBadge({ orderAction }: { orderAction: OrderActionDto }) {
  const { t } = useTranslation('tickets');
  const tone = toneOf(orderAction);
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${BADGE_CLASS[tone]}`}>
      {t(`order.status.${orderAction.status}`)}
    </span>
  );
}

// ── Result dialog (shown by the create wizard after a patch/depatch ticket) ───

const DIALOG_META: Record<Tone, { Icon: typeof CheckCircle2; ring: string; titleKey: string; msgKey: string }> = {
  success: { Icon: CheckCircle2, ring: 'bg-emerald-50 text-emerald-600', titleKey: 'order.result.success.title', msgKey: 'order.result.success.message' },
  failed: { Icon: XCircle, ring: 'bg-red-50 text-red-600', titleKey: 'order.result.failed.title', msgKey: 'order.result.failed.message' },
  pending: { Icon: Clock, ring: 'bg-amber-50 text-amber-600', titleKey: 'order.result.pending.title', msgKey: 'order.result.pending.message' },
  dryrun: { Icon: FlaskConical, ring: 'bg-blue-50 text-blue-600', titleKey: 'order.result.dryRun.title', msgKey: 'order.result.dryRun.message' },
  dryrunFailed: { Icon: FlaskConical, ring: 'bg-blue-50 text-blue-600', titleKey: 'order.result.dryRunFailed.title', msgKey: 'order.result.dryRunFailed.message' },
};

export function OrderResultDialog({
  orderAction, ticketNumber, open, onViewTicket,
}: {
  orderAction: OrderActionDto;
  ticketNumber: string;
  open: boolean;
  onViewTicket: () => void;
}) {
  const { t } = useTranslation('tickets');
  const tone = toneOf(orderAction);
  const meta = DIALOG_META[tone];
  const Icon = meta.Icon;
  const isDry = tone === 'dryrun' || tone === 'dryrunFailed';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onViewTicket(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex flex-col items-center text-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${meta.ring}`}>
              <Icon className="w-6 h-6" />
            </div>
            {isDry && (
              <span className="inline-flex items-center rounded-full border border-blue-300 bg-blue-50 px-2.5 py-0.5 text-xs font-bold tracking-wide text-blue-700">
                {t('order.dryRunBadge')}
              </span>
            )}
            <DialogTitle>{t(meta.titleKey)}</DialogTitle>
            <DialogDescription>
              {t(meta.msgKey, { number: ticketNumber, action: t(`order.action.${orderAction.action}`) })}
            </DialogDescription>
            {tone === 'failed' && orderAction.error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 font-mono break-all">
                {orderAction.error}
              </p>
            )}
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onViewTicket} className="w-full">{t('order.result.viewTicket')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail-page Order panel (inline status card + technical-log slide-over) ───

function LogRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <div className="mt-1 text-slate-700">{children}</div>
    </div>
  );
}

export function OrderActionPanel({
  orderAction, isInternal, ticketId, ticketOperatorCode, ticketSubcategory, onRetried,
}: {
  orderAction: OrderActionDto | null;
  isInternal: boolean;
  ticketId: string;
  ticketOperatorCode: string;
  ticketSubcategory: string | null;
  onRetried: () => void;
}) {
  const { t } = useTranslation('tickets');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [credOps, setCredOps] = useState<string[]>([]);
  const [runMode, setRunMode] = useState<'LIVE' | 'DRY_RUN' | 'OFF' | null>(null);
  const [runAsOp, setRunAsOp] = useState('');

  useEffect(() => {
    if (!isInternal) return;
    fetchCredentialedOperators()
      .then((r) => { setCredOps(r.data.operators || []); setRunMode(r.data.mode); })
      .catch(() => {});
  }, [isInternal]);

  const action = orderAction?.action ?? (ticketSubcategory === 'MANUAL_PATCH' ? 'PATCH' : 'CEASE');
  const actionLabel = t(`order.action.${action}`);
  // The mode this run will use: an existing row's IMMUTABLE mode, else the current effective mode.
  const effectiveMode = orderAction?.mode ?? runMode ?? 'DRY_RUN';
  const isDry = effectiveMode === 'DRY_RUN';

  // "Ticket operator; pick if none": run as the ticket's operator when it has creds, otherwise the
  // user must choose a configured operator (e.g. a Unifiber ticket run as Proximus).
  const ticketOpHasCreds = credOps.map((c) => c.toUpperCase()).includes((ticketOperatorCode || '').toUpperCase());
  const needsPicker = !ticketOpHasCreds;

  // Runnable by internal users. Existing terminal LIVE success → no; a first run while OFF → no.
  const canRun = isInternal && (orderAction
    ? !(orderAction.mode === 'LIVE' && (orderAction.status === 'SUCCESS' || orderAction.status === 'SENT'))
    : runMode !== null && runMode !== 'OFF');

  function openConfirm() {
    setRunAsOp(needsPicker && credOps.length === 1 ? credOps[0] : '');
    setConfirmOpen(true);
  }

  async function handleRun() {
    if (needsPicker && !runAsOp) return;
    setRetrying(true);
    try {
      await retryOrderActionApi(ticketId, needsPicker ? runAsOp : undefined);
      toast.success(t('order.panel.retrySuccess'));
      setConfirmOpen(false);
      setSheetOpen(false);
      onRetried();
    } catch (err: any) {
      toast.error(err?.message || t('order.panel.retryError'));
    } finally {
      setRetrying(false);
    }
  }

  // Button label: first run vs re-run (re-simulate in dry-run).
  const runLabel = !orderAction ? t('order.panel.run') : isDry ? t('order.panel.reSimulate') : t('order.panel.retry');
  const cardClass = orderAction && isDry
    ? 'border-blue-200 border-dashed bg-blue-50/30'
    : 'border-slate-200 bg-white';

  return (
    <div className={`rounded-xl border shadow-sm p-6 ${cardClass}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${orderAction && isDry ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>
            <Cable className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-slate-800">
                {t('order.panel.title')} · {actionLabel}
              </p>
              {orderAction && isDry && (
                <span className="inline-flex items-center rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-blue-700">
                  {t('order.dryRunBadge')}
                </span>
              )}
            </div>
            {orderAction ? (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                <OrderStatusBadge orderAction={orderAction} />
                <span>· {new Date(orderAction.createdAt).toLocaleString()}</span>
                {orderAction.attempts > 1 && <span>· {t('order.panel.attempts', { count: orderAction.attempts })}</span>}
              </div>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                {runMode === 'OFF' ? t('order.panel.offNote') : t('order.panel.notRunHint')}
              </p>
            )}
          </div>
        </div>
        {isInternal && (
          <div className="flex items-center gap-2 shrink-0">
            {canRun && (
              <Button variant={isDry ? 'outline' : 'default'} size="sm" onClick={openConfirm}>{runLabel}</Button>
            )}
            {orderAction && (
              <Button variant="outline" size="sm" onClick={() => setSheetOpen(true)}>{t('order.panel.viewLog')}</Button>
            )}
          </div>
        )}
      </div>

      {isInternal && orderAction && (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
            <SheetTitle>{t('order.panel.technicalLog')}</SheetTitle>
            <div className="mt-4 space-y-4 text-sm">
              <LogRow label={t('order.panel.mode')}>{orderAction.mode}</LogRow>
              <LogRow label={t('order.panel.statusLabel')}>{t(`order.status.${orderAction.status}`)}</LogRow>
              {orderAction.httpStatus != null && (
                <LogRow label={t('order.panel.httpStatus')}><span className="font-mono">{orderAction.httpStatus}</span></LogRow>
              )}
              {orderAction.endpoint && (
                <LogRow label={t('order.panel.endpoint')}><span className="font-mono text-xs break-all">{orderAction.endpoint}</span></LogRow>
              )}
              {orderAction.error && (
                <LogRow label={t('order.panel.error')}><span className="text-red-700">{orderAction.error}</span></LogRow>
              )}
              {orderAction.requestBody != null && (
                <LogRow label={t('order.panel.request')}>
                  <pre className="mt-1 bg-slate-50 border border-slate-100 p-2 rounded text-xs overflow-auto max-h-56">
                    {JSON.stringify(orderAction.requestBody, null, 2)}
                  </pre>
                </LogRow>
              )}
              {orderAction.responseBody != null && (
                <LogRow label={t('order.panel.response')}>
                  <pre className="mt-1 bg-slate-50 border border-slate-100 p-2 rounded text-xs overflow-auto max-h-56">
                    {JSON.stringify(orderAction.responseBody, null, 2)}
                  </pre>
                </LogRow>
              )}
            </div>
            {canRun && (
              <Button onClick={() => { setSheetOpen(false); openConfirm(); }} className="w-full mt-6">
                {runLabel}
              </Button>
            )}
          </SheetContent>
        </Sheet>
      )}

      {/* Confirmation dialog — internal users run/re-run the order on behalf of an operator. */}
      {isInternal && canRun && (
        <Dialog open={confirmOpen} onOpenChange={(o) => { if (!retrying) setConfirmOpen(o); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{isDry ? t('order.confirm.titleDry') : t('order.confirm.titleLive')}</DialogTitle>
              <DialogDescription>{t('order.confirm.body', { action: actionLabel })}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {isDry ? (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-blue-800">
                  {t('order.confirm.dryNote', { action: actionLabel })}
                </div>
              ) : (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800 flex gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{t('order.confirm.liveWarning', { action: actionLabel })}</span>
                </div>
              )}
              {needsPicker ? (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600">{t('order.confirm.runAs')}</label>
                  <Select value={runAsOp} onValueChange={setRunAsOp}>
                    <SelectTrigger><SelectValue placeholder={t('order.confirm.chooseOperator')} /></SelectTrigger>
                    <SelectContent>
                      {credOps.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {credOps.length === 0 && <p className="text-xs text-amber-700">{t('order.confirm.noOperators')}</p>}
                </div>
              ) : (
                <p className="text-slate-600">{t('order.confirm.usingOperator', { operator: ticketOperatorCode })}</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={retrying}>
                {t('order.confirm.cancel')}
              </Button>
              <Button
                onClick={handleRun}
                disabled={retrying || (needsPicker && !runAsOp)}
                variant={isDry ? 'default' : 'destructive'}
                className="flex items-center gap-2"
              >
                {retrying && <Loader2 className="w-4 h-4 animate-spin" />}
                {isDry ? t('order.confirm.confirmDry') : t('order.confirm.confirmLive')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
